import { useQueryClient } from '@tanstack/react-query';
import { productionKeys } from '../queryKeys';

/**
 * Everything that has to be refetched when ink physically moves.
 *
 * ONE copy, shared by the kit page, the trolley list and the press terminal.
 * There used to be two near-identical private copies and they drifted, which is
 * exactly how the bug below got in.
 *
 * The demand roll-up is the non-obvious member of this list. Since 11/09 it
 * carries `allocated_kg` — how much of each ink is already on an OPEN trolley
 * run — and the balance screen computes
 *
 *     balance = free stock + on order − (gross demand − allocated)
 *
 * so a demand response cached from BEFORE a kit reports allocated = 0 while the
 * freshly-refetched stock has already dropped the kitted tubs out of the store.
 * Both halves then say "gone", and the balance reads a large negative — Jack hit
 * exactly this on 14/09 (−36.7 kg for P2035FSCOLMQ straight after kitting a full
 * trolley). The backend was right the whole time; only the cache was stale.
 *
 * Demand now depends on trolley state, so anything that moves a tub on or off a
 * trolley MUST invalidate it. That is the rule this hook exists to enforce.
 */
export function useInkInvalidate() {
  const qc = useQueryClient();
  return (runId?: number) => {
    // Runs and trolleys
    qc.invalidateQueries({ queryKey: ['production', 'ink-trolley-runs'] });
    if (runId != null) qc.invalidateQueries({ queryKey: productionKeys.inkTrolleyRun(runId) });
    qc.invalidateQueries({ queryKey: productionKeys.inkLocations() });

    // Stock, batches and the movement journal
    qc.invalidateQueries({ queryKey: ['production', 'ink-stock'] });
    qc.invalidateQueries({ queryKey: ['production', 'ink-batches'] });
    qc.invalidateQueries({ queryKey: ['production', 'ink-ledger'] });
    qc.invalidateQueries({ queryKey: ['inkStoreSealedUnits'] });

    // Demand — because allocated_kg lives in it. See the note above; dropping
    // these two lines reintroduces the negative balance.
    qc.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
    qc.invalidateQueries({ queryKey: ['production', 'ink-demand-for-jobs'] });
  };
}
